import { Box, Icon, Popover, TextInput, useOpenClose } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import useKeyboardShortcut from 'use-keyboard-shortcut';

import { useLazySearchOrganizationsQuery } from '../services/organizations';
import { FlatOrganization, OrganizationNode } from '../types/Organizations';

import { useAuth } from './auth/authProvider';

const Result = ({
  item,
  handleClick,
  currentOrg,
}: {
  item: FlatOrganization;
  currentOrg: OrganizationNode | undefined;
  handleClick: (alias: string) => void;
}) => {
  const level = item.path.split('/').length - 1 - 1;
  return (
    <Box
      as="button"
      cursor="pointer"
      type="button"
      background="transparent"
      borderColor="separator"
      borderWidth="0 0 xs 0"
      color="body-primary"
      hover={{ background: 'secondary' }}
      radius="sm"
      title={item.alias}
      onClick={() => handleClick(item.alias)}
      textAlign="left"
      direction="row"
      justifyContent="space-between"
      padding="sm xs"
      alignItems="center"
    >
      <Box color="body-secondary" width="sm" margin="0 xs 0 0">
        {currentOrg?.id === item.id && <Icon name="nav-right" />}
      </Box>
      <Box flex="auto" childGap="md" direction="row" alignItems="center">
        <OrgBox orgName={item.name} level={level} />
        <Box direction="column" childGap="2xs">
          <Box width="3xl" fontWeight="medium">
            {item.name}
          </Box>
          <Box color="body-secondary" fontFamily="monospace">
            {item.path}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const OrgBox = ({ className, orgName, level = 0 }: { className?: string; orgName: string; level?: number }) => {
  const firstLettersOfName = orgName
    ? orgName
        .match(/\b(\w)/g)
        ?.join('')
        .toUpperCase()
        .slice(0, 2)
    : [];
  const boxLetters =
    firstLettersOfName && firstLettersOfName?.length > 2 ? firstLettersOfName?.slice(0, -1) : firstLettersOfName;
  const organizationColor =
    level === 0
      ? 'var(--color-brand-primary-400)'
      : level === 1
        ? 'var(--color-brand-secondary-400)'
        : level === 2
          ? 'var(--color-brand-danger-400)'
          : level === 3
            ? 'var(--color-brand-grey-400)'
            : level === 4
              ? 'var(--color-brand-secondary-600)'
              : 'var(--color-brand-tertiary-400)';

  return (
    <Box
      radius="sm"
      color="white"
      style={{ backgroundColor: organizationColor }}
      width="32px"
      height="25px"
      textAlign="center"
      padding={'2xs'}
      fontWeight="medium"
      className={className}
    >
      {boxLetters}
    </Box>
  );
};

const Row = ({
  item,
  level,
  handleClick,
  currentOrg,
}: {
  item: OrganizationNode;
  currentOrg: OrganizationNode | undefined;
  level: number;
  handleClick: (alias: string) => void;
}) => {
  const paddingLeft = `${(level + 1) * 8}px`;

  return (
    <>
      <Box
        as="button"
        cursor="pointer"
        type="button"
        padding={{ base: `xs sm` }}
        style={{
          paddingLeft: paddingLeft,
        }}
        background="transparent"
        borderWidth="0"
        color="body-primary"
        hover={{ background: 'secondary' }}
        radius="sm"
        title={item.alias}
        onClick={() => handleClick(item.alias)}
        textAlign="left"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box color="body-secondary" width="sm" margin="0 xs 0 0">
          {currentOrg?.id === item.id && <Icon name="nav-right" />}
        </Box>
        <Box flex="auto" childGap="xs" direction="row" alignItems="center">
          <OrgBox orgName={item.name} level={level} />
          <Box>{item.name}</Box>
        </Box>
      </Box>
      {item.children?.map((child: any) => (
        <Row key={child.id} item={child} level={level + 1} handleClick={handleClick} currentOrg={currentOrg} />
      ))}
    </>
  );
};

export function TreeView({
  items,
  handleClick,
  currentOrg,
}: {
  items: OrganizationNode[];
  handleClick: (alias: string) => void;
  currentOrg: OrganizationNode | undefined;
}) {
  return (
    <Box maxHeight="4xl" overflow="auto">
      {items?.map((item) => (
        <Row key={item.id} item={item} level={0} currentOrg={currentOrg} handleClick={handleClick} />
      ))}
    </Box>
  );
}

export function SearchView({
  flat,
  handleClick,
  currentOrg,
}: {
  flat: FlatOrganization[];
  handleClick: (alias: string) => void;
  currentOrg: OrganizationNode | undefined;
}) {
  return (
    <Box maxHeight="4xl" overflow="auto">
      {flat?.map((item) => (
        <Result key={item.id} item={item} currentOrg={currentOrg} handleClick={handleClick} />
      ))}
    </Box>
  );
}

export default function OrganizationMenu({ className }: { className?: string }) {
  const { isOpen, handleClose, handleToggle } = useOpenClose();
  const [value, setValue] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Lazy query for initial org tree — only fetched when popover opens
  const [triggerInitial, { data: initialData, isLoading: isInitialLoading, isError: isInitialError }] =
    useLazySearchOrganizationsQuery();

  // Lazy query only for text search
  const [triggerSearch, { data: searchData, isLoading: isSearchLoading }] = useLazySearchOrganizationsQuery();

  const activeData = debouncedSearchTerm ? searchData : initialData;
  const { organizations: items = [], flat = [] } = activeData ?? {};
  const isLoading = debouncedSearchTerm ? isSearchLoading : isInitialLoading;

  const { login, organizationData } = useAuth();

  const hasFetchedInitial = useRef(false);
  const fetchInitialIfNeeded = useCallback(() => {
    if (!hasFetchedInitial.current || isInitialError) {
      triggerInitial({ useRootOrganization: true })
        .unwrap()
        .then(() => {
          hasFetchedInitial.current = true;
        })
        .catch(() => {
          hasFetchedInitial.current = false;
        });
    }
  }, [triggerInitial, isInitialError]);

  const handleMenuToggle = useCallback(() => {
    fetchInitialIfNeeded();
    handleToggle();
  }, [fetchInitialIfNeeded, handleToggle]);

  const handleShortcutToggle = useCallback(() => {
    const active = document.activeElement;
    if (active && (active as HTMLElement).isContentEditable) return;
    handleMenuToggle();
  }, [handleMenuToggle]);

  const { flushHeldKeys } = useKeyboardShortcut(['Shift', 'O'], handleShortcutToggle, {
    overrideSystem: false,
    ignoreInputFields: true,
    repeatOnHold: false,
  });

  const closePopover = useCallback(() => {
    handleClose();
    setValue('');
    setDebouncedSearchTerm('');
    flushHeldKeys();
  }, [flushHeldKeys, handleClose]);

  const impersonateOrg = useCallback(
    (alias: string) => {
      login(undefined, alias);
    },
    [login],
  );

  const clearSearch = useCallback(() => {
    setValue('');
    setDebouncedSearchTerm('');
    return false;
  }, []);

  useEffect(() => {
    if (value.length >= 3) {
      const id = setTimeout(() => {
        setDebouncedSearchTerm(value);
        triggerSearch({ searchTerm: value, useRootOrganization: true });
      }, 400);
      return () => clearTimeout(id);
    }
    setDebouncedSearchTerm('');
  }, [value, triggerSearch]);

  const content = (
    <Box childGap="sm" fontSize="sm">
      <TextInput
        id="searchText"
        type="text"
        value={value}
        autoComplete="off"
        hideLabel
        label="Search for an organization"
        placeholder="Search for an organization"
        prefix={<Icon name="search" />}
        onChange={(event: any) => setValue(event.target.value)}
        onClear={clearSearch}
      />

      {value ? (
        <SearchView flat={flat} handleClick={impersonateOrg} currentOrg={organizationData} />
      ) : (
        <TreeView items={items} handleClick={impersonateOrg} currentOrg={organizationData} />
      )}
    </Box>
  );

  return (
    <Popover
      content={content}
      isOpen={isOpen}
      placement="bottom-start"
      offsetFromTarget={4}
      contentContainerProps={{
        padding: 'md',
        shadow: 'sm',
        borderColor: 'separator',
        borderWidth: 'xs',
        background: 'primary',
        radius: 'sm',
        color: 'body-primary',
        fontSize: 'sm',
        width: { base: '100', tablet: '80' },
        minWidth: '3xl',
      }}
      arrowColor="white"
      onClickOutside={closePopover}
      portalTarget={document.body}
      withPortal
      hasArrow={false}
    >
      <Box
        as="button"
        className={className}
        fontSize={{ base: 'sm', desktop: 'md' }}
        textAlign="left"
        type="button"
        childGap="2xs"
        cursor="pointer"
        direction="row"
        alignItems="center"
        onClick={handleMenuToggle}
        borderWidth="0"
        background="primary"
        color="body-primary"
        padding={{ base: 'xs sm', desktop: 'sm' }}
        radius="md"
        hover={{
          color: 'contrast',
        }}
      >
        {!isLoading && (
          <>
            <Box fontSize="sm">{organizationData?.name}</Box>
            <Icon name="caret-sm-down" />
          </>
        )}
      </Box>
    </Popover>
  );
}
